import { getPath } from "src/basepath";
import { STORAGE_KEY } from "src/storage/useSearchStorage.constants";
import { AppStore } from "../jotai";
import { accessTokenAtom, refreshTokenAtom, tokenAtom } from "./auth-atoms";
import { TokenResponseSchema } from "./auth-schema";

export interface AuthResponse {
  refresh_token: string | null;
  access_token: string | null;
  message: string | null;
}

export interface DeploymentRedirectResponse {
  redirect_url: string;
}

let refreshingPromise = null as Promise<AuthResponse | void> | null;
let validateTokenPromise = null as Promise<AuthResponse> | null;

export const authService = {
  async login(
    username: string,
    password: string
  ): Promise<AuthResponse | DeploymentRedirectResponse | { message: string }> {
    const response = await fetch(getPath("/api/auth/token"), {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      credentials: "include",
      body: new URLSearchParams({
        username,
        password,
        grant_type: "password",
      }),
    });

    if (!response.ok) {
      const json = await response.json();
      throw new Error(json.message ?? json.detail ?? "Login failed");
    }

    const resData = await response.json();

    const { data: tokenResponse, success } = TokenResponseSchema.safeParse(resData);

    if (!success) {
      return resData;
    }

    AppStore.set(refreshTokenAtom, tokenResponse.refresh_token);
    AppStore.set(accessTokenAtom, tokenResponse.access_token);

    return { message: "Login successful" };
  },

  refresh: async (): Promise<AuthResponse | void> => {
    if (refreshingPromise) {
      return refreshingPromise;
    }

    refreshingPromise = fetch(getPath("/api/auth/refresh"), {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        refresh_token: AppStore.get(refreshTokenAtom) + "",
      },
    }).then(async (res) => {
      const authResponse = await res.json();
      if (!res.ok) {
        throw new Error(authResponse.message || "Token refresh failed");
      }

      if (!authResponse) {
        refreshingPromise = null;
        throw new Error("Token refresh failed");
      }

      AppStore.set(accessTokenAtom, authResponse.access_token);
      AppStore.set(refreshTokenAtom, authResponse.refresh_token);

      refreshingPromise = null;
      return authResponse;
    }) as Promise<AuthResponse | void>;

    return refreshingPromise;
  },

  async logout(): Promise<void> {
    AppStore.set(accessTokenAtom, null);
    AppStore.set(refreshTokenAtom, null);
    localStorage.removeItem(STORAGE_KEY);
    try {
      await fetch(getPath("/api/auth/token"), {
        method: "DELETE",
        credentials: "include",
      });
    } finally {
      window.location.replace(getPath("/login"));
    }
  },

  async validateToken(refresh_token: string): Promise<AuthResponse> {
    if (validateTokenPromise) {
      return validateTokenPromise;
    }

    validateTokenPromise = fetch(getPath("/api/auth/refresh"), {
      method: "PUT",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token }),
    }).then(async (res) => {
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.message || "Token refresh failed");
      }

      const authResponse = (await res.json()) as AuthResponse;

      validateTokenPromise = null;

      if (!authResponse) {
        throw new Error("Token refresh failed");
      }

      AppStore.set(accessTokenAtom, authResponse.access_token);
      AppStore.set(refreshTokenAtom, authResponse.refresh_token);

      return authResponse;
    }) as Promise<AuthResponse>;

    return validateTokenPromise;
  },

  storeSession(session?: AuthResponse | null) {
    if (!session) {
      AppStore.set(accessTokenAtom, null);
      AppStore.set(refreshTokenAtom, null);
      return;
    }

    if (session.access_token) {
      AppStore.set(accessTokenAtom, session.access_token);
    }
    if (session.refresh_token) {
      AppStore.set(refreshTokenAtom, session.refresh_token);
    }
  },

  async refreshAccessTokenIfExpired() {
    const token = AppStore.get(tokenAtom);
    if (!token) {
      await authService.logout();
      throw new Error("No token found");
    }

    const secondsUntilExpiration = token.exp - Date.now() / 1000;

    if (secondsUntilExpiration < 60) {
      try {
        await authService.refresh();
      } catch (error) {
        if (error instanceof Error) {
          if (
            ["Refresh token expired", "Revoked refresh token"].includes(
              error.message
            )
          ) {
            void authService.logout();
            throw new Error(error.message);
          }
        }
        throw error;
      }
    }
  },
};
